
import { AfterViewChecked, Component, ElementRef, Input, ViewChild, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-html-editor',
  imports: [FormsModule, MatIconModule],
  template: `
    <div class="editor-toolbar">
      <button type="button" (click)="exec('bold')" title="Bold" [disabled]="showingSource"><b>B</b></button>
      <button type="button" (click)="exec('italic')" title="Italic" [disabled]="showingSource"><i>I</i></button>
      <button type="button" (click)="exec('underline')" title="Underline" [disabled]="showingSource"><u>U</u></button>
      <button type="button" (click)="exec('insertUnorderedList')" title="Bullet List" [disabled]="showingSource">• List</button>
      <button type="button" (click)="exec('insertOrderedList')" title="Numbered List" [disabled]="showingSource">1. List</button>
      <select (change)="setFontSize($event)" title="Font Size" [disabled]="showingSource">
        <option value="">Size</option>
        @for (s of fontSizes; track s) {
          <option [value]="s.value">{{ s.label }}</option>
        }
      </select>
      <input type="color" (change)="setFontColor($event)" title="Font Color" [disabled]="showingSource" />
      <button type="button" (click)="exec('justifyLeft')" title="Align Left" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">format_align_left</mat-icon>
      </button>
      <button type="button" (click)="exec('justifyCenter')" title="Align Center" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">format_align_center</mat-icon>
      </button>
      <button type="button" (click)="exec('justifyRight')" title="Align Right" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">format_align_right</mat-icon>
      </button>
      <button type="button" (click)="exec('justifyFull')" title="Justify" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">format_align_justify</mat-icon>
      </button>
      <button type="button" (click)="showTableDialog = true" title="Insert Table" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">table_chart</mat-icon>
      </button>
      <button type="button" (click)="showEmotionsDropdown = !showEmotionsDropdown" title="Insert Emotion" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">sentiment_very_satisfied</mat-icon>
      </button>
      <button type="button" (click)="showFileDialog = true" title="Insert File Link" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">attach_file</mat-icon>
      </button>
      <button type="button" (click)="showImageDialog = true" title="Insert Image" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">image</mat-icon>
      </button>
      <button type="button" (click)="clearFormatting()" title="Clear Formatting" [disabled]="showingSource">
        <mat-icon fontSet="material-icons">format_clear</mat-icon>
      </button>
      <button type="button" (click)="toggleSource()" title="Pokaż źródło">
        <mat-icon fontSet="material-icons">code</mat-icon>
      </button>
      <!-- Table dialog -->
      @if (showTableDialog) {
        <div class="table-dialog">
          <label>Cols: <input type="number" min="1" [(ngModel)]="tableCols" style="width:40px"></label>
          <label>Rows: <input type="number" min="1" [(ngModel)]="tableRows" style="width:40px"></label>
          <button type="button" (click)="insertTable()">Insert</button>
          <button type="button" (click)="showTableDialog = false">Cancel</button>
        </div>
      }
      <!-- File dialog -->
      @if (showFileDialog) {
        <div class="file-dialog">
          <label>File name: <input type="text" [(ngModel)]="fileNameHashed" placeholder="hash.ext" style="width:180px"></label>
          <label>Link text: <input type="text" [(ngModel)]="fileLinkText" placeholder="Download file" style="width:180px"></label>
          <button type="button" (click)="insertFileLink()">Insert</button>
          <button type="button" (click)="cancelFileDialog()">Cancel</button>
        </div>
      }
      <!-- Image dialog -->
      @if (showImageDialog) {
        <div class="image-dialog">
          <label>File name: <input type="text" [(ngModel)]="imageFileName" placeholder="hash.ext" style="width:180px"></label>
          <label class="image-option">
            <input type="radio" name="imageSizeMode" [(ngModel)]="imageSizeMode" value="full">
            Full size
          </label>
          <label class="image-option">
            <input type="radio" name="imageSizeMode" [(ngModel)]="imageSizeMode" value="width">
            Width
          </label>
          @if (imageSizeMode === 'width') {
            <div class="size-input-group">
              <input type="number" min="1" [(ngModel)]="imageSize" placeholder="value" style="width:80px">
              <select [(ngModel)]="imageSizeUnit" style="width:70px">
                <option value="px">px</option>
                <option value="%">%</option>
              </select>
            </div>
          }
          <label class="image-option">
            <input type="radio" name="imageSizeMode" [(ngModel)]="imageSizeMode" value="height">
            Height
          </label>
          @if (imageSizeMode === 'height') {
            <input type="number" min="1" [(ngModel)]="imageSize" placeholder="px" style="width:80px">
          }
          <label class="image-option">
            <input type="checkbox" [(ngModel)]="imageOpenInNewTab">
            Link to open
          </label>
          <button type="button" (click)="insertImage()">Insert</button>
          <button type="button" (click)="cancelImageDialog()">Cancel</button>
        </div>
      }
      <!-- Emotions dropdown -->
      @if (showEmotionsDropdown) {
        <div class="emotions-dropdown">
          @for (emotion of emotions; track emotion) {
            <button type="button" (click)="insertEmotion(emotion)" class="emotion-btn">
              {{ emotion }}
            </button>
          }
        </div>
      }
    </div>
    @if (!showingSource) {
      <div
        class="editor-content"
        contenteditable="true"
        #editable
        [style.minHeight.px]="height"
        (input)="onInput($event)"
        (keyup)="saveSelection()"
        (mouseup)="saveSelection()"
        (blur)="onTouched()"
        style="min-height:120px; border:1px solid #ccc; padding:8px; border-radius:4px; background:#fff;"
      ></div>
    }
    @if (showingSource) {
      <textarea
        class="editor-content"
        [style.minHeight.px]="height"
        style="min-height:120px; border:1px solid #ccc; padding:8px; border-radius:4px; background:#fff; width:100%;"
        [(ngModel)]="value"
        (ngModelChange)="onSourceChange($event)"
      ></textarea>
    }
    `,
  styles: [`
    .editor-toolbar {
      border: 1px solid #ccc;
      border-bottom: none;
      background: #f8f8f8;
      padding: 4px;
      border-radius: 4px 4px 0 0;
      display: flex;
      gap: 4px;
      align-items: center;
      flex-wrap: wrap;
      position: relative;
    }
    .editor-toolbar button, .editor-toolbar select, .editor-toolbar input[type="color"] {
      margin-right: 2px;
      cursor: pointer;
      background: none;
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 2px 6px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .editor-content {
      max-height: 550px;
      overflow-y: auto;
    }

    .editor-content:focus {
      outline: 1px solidrgb(18, 126, 9);
    }
    .editor-toolbar input[type="color"] {
      width: 32px;
      min-width: 32px;
      max-width: 32px;
      height: 32px;
      padding: 0;
    }
    .table-dialog {
      position: absolute;
      top: 36px;
      left: 0;
      background: #fff;
      border: 1px solid #ccc;
      padding: 8px;
      border-radius: 4px;
      z-index: 10;
      display: flex;
      gap: 8px;
      align-items: center;
    }
    .file-dialog {
      position: absolute;
      top: 36px;
      left: 0;
      background: #fff;
      border: 1px solid #ccc;
      padding: 8px;
      border-radius: 4px;
      z-index: 10;
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
      max-width: 520px;
    }
    .file-dialog label {
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .image-dialog {
      position: absolute;
      top: 36px;
      left: 0;
      background: #fff;
      border: 1px solid #ccc;
      padding: 8px;
      border-radius: 4px;
      z-index: 10;
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
      max-width: 620px;
    }
    .image-option {
      font-size: 13px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .size-input-group {
      display: inline-flex;
      gap: 4px;
      align-items: center;
    }
    .table-dialog label {
      font-size: 13px;
    }
    .emotions-dropdown {
      position: absolute;
      top: 36px;
      left: 0;
      background: #fff;
      border: 1px solid #ccc;
      padding: 8px;
      border-radius: 4px;
      z-index: 10;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      max-width: 300px;
    }
    .emotion-btn {
      background: none;
      border: 1px solid #eee;
      padding: 4px 8px;
      cursor: pointer;
      font-size: 20px;
      border-radius: 3px;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .emotion-btn:hover {
      background: #f0f0f0;
      border-color: #ccc;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    }
  ]
})
export class HtmlEditorComponent implements ControlValueAccessor, AfterViewChecked {
  @Input() value = '';
  @Input() height: number = 120;

  @ViewChild('editable') editable!: ElementRef<HTMLDivElement>;

  fontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'X-Large', value: '7' }
  ];

  emotions = ['😀', '😂', '😍', '😢', '😡', '😎', '🤔', '😴', '🎉', '❤️', '👍', '👏', '🔥', '⭐', '✨', '😘', '🥰', '😆', '😅', '😌', '😜', '🤣', '😱', '😰', '😤', '🤐', '😷', '🤒', '🤢', '🤮', '🤢', '😈', '👿', '💀', '💥', '👋', '🤚', '✋', '🖐️', '👌', '🤌', '🤟', '✌️', '🤞', '🫡', '💪', '🦾', '🦿', '👋', '🙌', '👐', '🤲', '🤝', '🙏', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '💌', '🎁', '🎈', '🎀', '🎊', '🎉', '🎂', '🍕', '🍔', '🍟', '🌭', '🍿', '🥓', '🥞', '🧇', '🥐', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳', '🧈', '🥞', '🥓', '🥞', '🥞', '🍗', '🍖', '🌭', '🍔', '🍟', '🍕', '🌮', '🌯', '🥙', '🧆', '🍱', '🍘', '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥', '🥠', '🍚', '🍣', '🥟', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '☕', '🍵', '🍶', '🍾', '🍷', '🍸', '🍹', '🍺', '🍻', '🥂', '🥃', '🥤', '🧃', '🧉', '🧊', '✈️', '🚁', '🚂', '🚃', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋', '🚌', '🚍', '🚎', '🚐', '🚑', '🚒', '🚓', '🚔', '🚕', '🚖', '🚗', '🚘', '🚙', '🚚', '🚛', '🚜', '🏎️', '🏍️', '💨', '🚲', '🛴', '🛹', '🛼', '🚏', '⛽', '🚨', '🚥', '🚦', '🛑'];

  private needsContentSync = false;
  private savedRange: Range | null = null;
  showTableDialog = false;
  showingSource = false;
  showEmotionsDropdown = false;
  showFileDialog = false;
  showImageDialog = false;
  tableCols = 2;
  tableRows = 1;
  fileNameHashed = '';
  fileLinkText = '';
  imageFileName = '';
  imageSizeMode: 'full' | 'width' | 'height' = 'full';
  imageSize: number | null = null;
  imageSizeUnit: 'px' | '%' = 'px';
  imageOpenInNewTab = true;

  onChange = (_: any) => { };
  onTouched = () => { };

  exec(command: string) {
    if (command === 'justifyCenter') {
      if (this.wrapSelectionWithCenter()) {
        this.emitValue();
        return;
      }
    }
    document.execCommand(command, false);
    this.emitValue();
  }

  setFontSize(event: any) {
    const size = event.target.value;
    if (size) {
      document.execCommand('fontSize', false, size);
      this.emitValue();
    }
    event.target.value = '';
  }

  setFontColor(event: any) {
    const color = event.target.value;
    document.execCommand('foreColor', false, color);
    this.emitValue();
  }

  onInput(event: any) {
    this.value = event.target.innerHTML;
    this.onChange(this.value);
  }

  toggleSource() {
    this.showingSource = !this.showingSource;
    if (this.showingSource && this.editable) {
      // When switching to source mode, update value from editor
      this.value = this.editable.nativeElement.innerHTML;
    }
    if (!this.showingSource) {
      // Mark that we need to sync content after view updates
      this.needsContentSync = true;
    }
  }

    ngAfterViewChecked() {
    if (this.needsContentSync && this.editable) {
      this.editable.nativeElement.innerHTML = this.value || '';
      this.needsContentSync = false;
    }
  }

  onSourceChange(val: string) {
    this.value = val;
    this.onChange(this.value);
    if (this.editable && !this.showingSource) {
      this.editable.nativeElement.innerHTML = this.value;
    }
  }

  toggleTableDialog() {
    this.showTableDialog = !this.showTableDialog;
  }

  insertTable() {
    const cols = Math.max(1, this.tableCols);
    const rows = Math.max(1, this.tableRows);
    const colWidth = (100 / cols).toFixed(2) + '%';
    let table = '<table style="border-collapse:collapse;width:100%">';
    for (let r = 0; r < rows; r++) {
      table += '<tr>';
      for (let c = 0; c < cols; c++) {
        table += `<td style="border:1px solid #ccc;padding:4px;width:${colWidth};">&nbsp;</td>`;
      }
      table += '</tr>';
    }
    table += '</table><br>';
    this.insertHtml(table);
    this.showTableDialog = false; // Only close dialog here
    this.emitValue();
  }

  insertHtml(html: string) {
    this.restoreSelection();
    document.execCommand('insertHTML', false, html);
  }

  emitValue() {
    const el = (event?.target as HTMLElement)?.closest('.editor-content') as HTMLElement
      || document.activeElement as HTMLElement;
    if (el && el.classList.contains('editor-content')) {
      this.value = el.innerHTML;
      this.onChange(this.value);
    } else {
      // fallback: find the editor-content div
      const editor = document.querySelector('.editor-content') as HTMLElement;
      if (editor) {
        this.value = editor.innerHTML;
        this.onChange(this.value);
      }
    }
  }

  clearFormatting() {
    document.execCommand('removeFormat', false);
    document.execCommand('unlink', false);
    this.emitValue();
  }

  insertEmotion(emotion: string) {
    this.insertHtml(emotion);
    this.showEmotionsDropdown = false;
    this.emitValue();
  }

  insertFileLink() {
    const filename = (this.fileNameHashed || '').trim();
    const linkText = (this.fileLinkText || '').trim() || filename;
    if (!filename) {
      return;
    }
    const href = this.buildFileUrl(filename);
    const link = `<a href="${href}" target="_blank" rel="noopener">${this.escapeHtml(linkText)}</a>`;
    this.insertHtml(link);
    this.emitValue();
    this.fileNameHashed = '';
    this.fileLinkText = '';
    this.showFileDialog = false;
  }

  cancelFileDialog() {
    this.showFileDialog = false;
  }

  insertImage() {
    const filename = (this.imageFileName || '').trim();
    if (!filename) {
      return;
    }
    const src = this.buildFileUrl(filename);
    let sizeAttr = '';
    const sizeValue = this.imageSize && this.imageSize > 0 ? this.imageSize : null;
    if (this.imageSizeMode === 'width' && sizeValue) {
      sizeAttr = ` width=\"${sizeValue}${this.imageSizeUnit}\"`;
    } else if (this.imageSizeMode === 'height' && sizeValue) {
      sizeAttr = ` height=\"${sizeValue}px\"`;
    }
    const img = `<img src=\"${src}\" alt=\"\"${sizeAttr} />`;
    const html = this.imageOpenInNewTab
      ? `<a href=\"${src}\" target=\"_blank\" rel=\"noopener\">${img}</a>`
      : img;
    this.insertHtml(html);
    this.emitValue();
    this.imageFileName = '';
    this.imageSize = null;
    this.imageSizeMode = 'full';
    this.imageSizeUnit = 'px';
    this.imageOpenInNewTab = true;
    this.showImageDialog = false;
  }

  cancelImageDialog() {
    this.showImageDialog = false;
  }

  saveSelection() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      this.savedRange = selection.getRangeAt(0).cloneRange();
    }
  }

  private restoreSelection() {
    if (this.editable) {
      this.editable.nativeElement.focus();
    }
    const selection = window.getSelection();
    if (selection && this.savedRange) {
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }
  }

  private wrapSelectionWithCenter(): boolean {
    this.restoreSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return false;
    }
    const range = selection.getRangeAt(0);
    const node = selection.anchorNode;
    const element = node
      ? (node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement)
      : null;
    if (element) {
      const closest = element.closest('a, img');
      if (closest && closest.parentElement) {
        const wrapTarget = closest.tagName.toLowerCase() === 'img' && closest.parentElement.tagName.toLowerCase() === 'a'
          ? closest.parentElement
          : closest;
        const center = document.createElement('center');
        wrapTarget.parentElement.insertBefore(center, wrapTarget);
        center.appendChild(wrapTarget);
        return true;
      }
    }
    if (range.collapsed) {
      return false;
    }
    const container = document.createElement('div');
    container.appendChild(range.cloneContents());
    const html = container.innerHTML;
    document.execCommand('insertHTML', false, `<center>${html}</center>`);
    return true;
  }

  private buildFileUrl(filename: string): string {
    const base = (environment.FilesUrl || '').replace(/\/$/, '');
    return `${base}/mics/${encodeURIComponent(filename)}`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  writeValue(val: any): void {
    this.value = val || '';
    if (this.editable) {
      this.editable.nativeElement.innerHTML = this.value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}